import { useDialog } from '@/common/dialog/DialogContext';
import { EntityCardList } from '@/common/entityFramework/entityCardList/EntityCardList';
import { EntityFormDialog } from '@/common/entityFramework/entityForm/EntityFormDialog';
import { ConfirmDeleteDialog } from '@/common/entityFramework/entityList/ConfirmDeleteDialog';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { EntityPropForm } from './EntityPropForm';
import { buildPropIdFilter } from './propIdFilterUtils';

// One card per ENTITY_PROP row for a given owner Entity (e.g. a Program or Program Product); adding/editing
// happens in a dialog, so nothing here navigates away - mirrors the Add Product / Add Tier dialogs.
// typeFilterValues narrows the Prop picker to Props whose "filter" is unset, "ALL", or one of these values
// (e.g. the owner's Program Type or Calc Type).
export const EntityPropCardList = ({
  parentEntity,
  parentEntityObj,
  canEdit,
  typeFilterValues,
  title = 'Properties',
}) => {
  const { openDialog } = useDialog();
  const { navigateTo } = useNavState();
  const { hasRole } = usePrincipal();

  const [highlightId, setHighlightId] = useState(null);

  const canSave = canEdit && hasRole('MD_ENTITY_PROP_SAVE');
  const canDelete = canEdit && hasRole('MD_ENTITY_PROP_DELETE');

  const augRowsReq = useCallback(
    (rowsReq) => {
      rowsReq.expr = ExprUtils.whereParams('entity', parentEntity, 'entityId', parentEntityObj.id);
    },
    [parentEntity, parentEntityObj.id],
  );

  const filterValues = (typeFilterValues ?? []).filter((value) => value != null);
  const propIdFilter = buildPropIdFilter(filterValues);

  const openPropDialog = (mode, entityProp) => {
    const initialValues =
      mode === EntityFormMode.ADD ? { entity: parentEntity, entityId: parentEntityObj.id } : undefined;

    openDialog(EntityFormDialog, {
      title: `${mode === EntityFormMode.ADD ? 'Add' : 'Edit'} Property — ${parentEntityObj.code}`,
      module: 'md',
      entity: 'ENTITY_PROP',
      mode,
      entityId: entityProp?.id,
      initialValues,
      maxWidth: 'xl',
      renderForm: (formProps) => (
        <EntityPropForm ownerObj={parentEntityObj} propIdFilter={propIdFilter} {...formProps} />
      ),
    }).then((saved) => {
      if (saved) setHighlightId(saved.id);
    });
  };

  const contextMenuItems = (row) => {
    const items = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo('md/entity-prop', { mode: Mode.DETAIL, entityId: row.id }),
      },
    ];
    if (canDelete) {
      items.push({
        name: 'Delete',
        icon: fas.faTrash,
        disabled: row.status === 'APPROVED' || row.status === 'APPROVING',
        action: () => openDialog(ConfirmDeleteDialog, { data: { module: 'md', entity: 'ENTITY_PROP', id: row.id } }),
      });
    }
    return items;
  };

  const renderSummary = (entityProp) => (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
        <Typography fontWeight={600} noWrap>
          {entityProp.propName ?? entityProp.propCode}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ flex: '1 1 160px' }} noWrap>
        {entityProp.value}
      </Typography>
      {canSave && (
        <Stack direction="row" sx={{ ml: 'auto' }} onClick={(event) => event.stopPropagation()}>
          <Tooltip title="Edit property">
            <IconButton size="small" onClick={() => openPropDialog(EntityFormMode.EDIT, entityProp)}>
              <FontAwesomeIcon icon={fas.faPencil} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );

  const headerButtons = canSave && (
    <Button
      variant="contained"
      size="small"
      startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
      onClick={() => openPropDialog(EntityFormMode.ADD)}
    >
      Add property
    </Button>
  );

  const emptyState = (
    <Stack spacing={2} alignItems="center">
      <Typography color="text.secondary">{canEdit ? 'No properties yet.' : 'This has no properties.'}</Typography>
      {canSave && (
        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
          onClick={() => openPropDialog(EntityFormMode.ADD)}
        >
          Add property
        </Button>
      )}
    </Stack>
  );

  return (
    <EntityCardList
      module="md"
      entity="ENTITY_PROP"
      title={title}
      augRowsReq={augRowsReq}
      sortRows={(a, b) => String(a.propName ?? '').localeCompare(String(b.propName ?? ''))}
      hideSort
      renderSummary={renderSummary}
      contextMenuItems={contextMenuItems}
      headerButtons={headerButtons}
      emptyState={emptyState}
      highlightId={highlightId}
    />
  );
};
